/*
 * Sticky Add to Cart bar. Ported from the Danstack store's implementation so
 * the behaviour and position match; renamed to <nische-sticky-atc> so it can
 * never collide with a same-named element if Dawn or an app ever ships one.
 *
 * Deliberately owns no cart logic. It watches the real submit button inside
 * <product-form> and forwards clicks to it, so variant selection, quantity,
 * sold-out state and Dawn's AJAX cart drawer keep working from one place.
 */
class NischeStickyAtc extends HTMLElement {
  connectedCallback() {
    this.targetButton = document.querySelector(this.dataset.target);
    this.submitButton = this.querySelector('[data-sticky-submit]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.discountEl = this.querySelector('[data-sticky-discount]');
    this.priceSource = document.querySelector(this.dataset.priceSource);

    if (!this.targetButton || !this.submitButton) return;

    this.submitButton.addEventListener('click', this.forwardClick.bind(this));

    // Show the bar only once the real button has been scrolled PAST — not
    // merely whenever it is off screen. `isIntersecting` alone is false in both
    // directions, so on mobile (where the gallery pushes the button below the
    // fold) the bar would be up before the shopper had even reached it.
    // A negative top means the button is above the viewport: genuinely passed.
    this.observer = new IntersectionObserver(([entry]) => this.evaluate(entry.boundingClientRect), {
      rootMargin: '0px 0px -80px 0px',
    });
    this.observer.observe(this.targetButton);

    // The observer alone is not enough. It only fires when the intersection
    // ratio changes, and an INSTANT jump (scroll-to-top, an anchor link, the
    // browser restoring a scroll position) can take the button from
    // off-screen-above to off-screen-below without it ever crossing the
    // viewport — ratio stays 0 the whole way, no callback, and the bar stays
    // stuck open at the top of the page. This passive listener closes that
    // gap; it is rAF-throttled and reads one rect, so it stays cheap.
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.evaluate(this.targetButton.getBoundingClientRect());
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.syncState();

    // Dawn re-renders the buy button and the price block on variant change,
    // so both are watched rather than read once at load — otherwise the bar
    // would keep showing a stale price and stay enabled on a sold-out variant.
    this.buttonWatcher = new MutationObserver(this.syncState.bind(this));
    this.buttonWatcher.observe(this.targetButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });

    if (this.priceSource) {
      this.priceWatcher = new MutationObserver(this.syncPrice.bind(this));
      this.priceWatcher.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.buttonWatcher?.disconnect();
    this.priceWatcher?.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  // Single source of truth for "has the button been scrolled past?", so the
  // observer and the scroll listener can never disagree.
  evaluate(rect) {
    this.toggle(rect.bottom <= 0);
  }

  toggle(visible) {
    this.classList.toggle('is-visible', visible);
    // Lets the floating WhatsApp button lift clear of the bar instead of
    // sitting behind it.
    document.body.classList.toggle('sticky-atc-visible', visible);
  }

  forwardClick(event) {
    event.preventDefault();
    this.targetButton.click();
  }

  syncState() {
    const disabled = this.targetButton.hasAttribute('disabled');
    this.submitButton.toggleAttribute('disabled', disabled);

    // On this theme the real button is <button><span class="nis-buy__add-label">
    // Add to cart</span><span class="nis-buy__arrow">…</span><spinner/></button>,
    // so the FIRST span is the label — same lookup as the original, but worth
    // stating since this markup has two more spans after it.
    const label = this.targetButton.querySelector('span');
    if (label) this.submitButton.textContent = label.textContent.trim();

    this.syncPrice();
  }

  syncPrice() {
    if (!this.priceSource || !this.priceEl) return;
    // Prefer the sale price when the product is discounted, so the bar shows
    // what the shopper actually pays rather than the compare-at figure.
    const sale = this.priceSource.querySelector('.price__sale .price-item--sale');
    const regular = this.priceSource.querySelector('.price__regular .price-item--regular');
    const source = this.priceSource.classList.contains('price--on-sale') || sale ? sale : regular;
    const text = (source || regular || sale)?.textContent.trim();
    if (text) this.priceEl.textContent = text;

    // Mirror the "X% OFF" pill from the main price block (.nis-price__off)
    // rather than recomputing it — one source of truth, and it already
    // updates correctly on variant change since Dawn re-renders that whole
    // block. Hidden (not just emptied) when the variant isn't discounted, so
    // no dangling "% OFF" for a $0 saving.
    if (this.discountEl) {
      const pill = this.priceSource.querySelector('.nis-price__off');
      if (pill) {
        this.discountEl.textContent = pill.textContent.trim();
        this.discountEl.hidden = false;
      } else {
        this.discountEl.hidden = true;
      }
    }
  }
}

if (!customElements.get('nische-sticky-atc')) {
  customElements.define('nische-sticky-atc', NischeStickyAtc);
}
